import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

class ProductAvailability extends Component {
  /** @type {string|null} Track last-processed source to avoid double-update */
  #lastProcessedSourceId = null;
  /** @type {WeakSet<Event>} Deduplicates events even when sourceId is null/undefined */
  #processedEvents = new WeakSet();

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateAvailability);
    // Also listen at document level for cross-section stitching swaps.
    document.addEventListener(ThemeEvents.variantUpdate, this.updateAvailability);

    // When out of stock the ATC button is `disabled`, so the browser never
    // fires a 'click' event on it. Instead listen for 'pointerup' on the
    // document (fires on disabled buttons' parent) and 'click' on the
    // add-to-cart-component wrapper (which IS clickable).
    document.addEventListener('pointerup', this.#interceptAddToCartPointerUp);

    // Directly wire the notify-link inside this block so clicking it
    // triggers the Klaviyo BIS popup (triggerclick on product-availability__notify-link).
    this.addEventListener('click', this.#interceptNotifyLinkClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateAvailability);
    document.removeEventListener(ThemeEvents.variantUpdate, this.updateAvailability);
    document.removeEventListener('pointerup', this.#interceptAddToCartPointerUp);
    this.removeEventListener('click', this.#interceptNotifyLinkClick);
  }

  /**
   * Intercepts pointerup on or around the add-to-cart button area.
   * When the product is out of stock (notify-link is present in this block),
   * clicking anywhere on the disabled ATC button fires pointerup on its parent.
   * We detect that and trigger the notify-link click instead.
   * @param {PointerEvent} event
   */
  #interceptAddToCartPointerUp = (event) => {
    // Match click on the disabled button itself OR its wrapper (add-to-cart-component / .klaviyo-bis-trigger)
    const target = event.target;
    const atcWrapper = target?.closest('add-to-cart-component, .klaviyo-bis-trigger');
    if (!atcWrapper) return;

    // Discontinued products must never trigger Notify Me
    const bisTrigger = atcWrapper.querySelector('[ref="klaviyoBisTrigger"]') || atcWrapper.closest('[ref="klaviyoBisTrigger"]');
    if (bisTrigger?.getAttribute('data-discontinued') === 'true' || atcWrapper.querySelector('.button--discontinued')) return;

    // Only act when the actual button inside is disabled (= out of stock)
    const btn = atcWrapper.querySelector('button[name="add"]');
    if (!btn || !btn.disabled) return;

    // Find the notify-link inside THIS availability block
    const notifyLink = this.querySelector('.product-availability__notify-link');
    if (!notifyLink) return;

    // Guard: ensure the ATC wrapper is in the same section as this availability block
    const availabilitySection = this.closest('.shopify-section, dialog');
    const atcSection = atcWrapper.closest('.shopify-section, dialog');
    if (availabilitySection && atcSection && availabilitySection !== atcSection) return;

    // Trigger the Notify Me popup via the notify-link
    notifyLink.click();
  };

  /**
   * Directly handles clicks on the product-availability__notify-link button
   * inside this custom element, triggering the Klaviyo Back-in-Stock popup.
   * Acts as a triggerclick bound at the element level (complements the
   * document-level delegation in klaviyo-bis-bridge.js).
   * @param {MouseEvent} event
   */
  #interceptNotifyLinkClick = (event) => {
    const link = event.target?.closest('.product-availability__notify-link');
    if (!link) return;
    // The klaviyo-bis-bridge.js document listener handles the actual popup
    // open — prevent any default behaviour and let the event bubble so that
    // listener fires naturally. Nothing extra needed here; this handler exists
    // so the element has an explicit, inspectable triggerclick binding for
    // the notify-link, matching the pattern used for the ATC button intercept.
    event.preventDefault();
  };

  /**
   * Updates stock + click & collect availability to match the newly selected variant.
   * @param {CustomEvent} event - The variant update event.
   */
  updateAvailability = (event) => {
    // Deduplication: when this element's closest section is also an ancestor
    // of the element that dispatched the event, both the section-scoped and
    // document-level listeners above fire for the SAME event as it bubbles.
    // morph() reuses (moves) nodes out of `event.detail.data.html` into the
    // live DOM rather than cloning them, so a second morph pass against the
    // now-depleted source tree strips content the first pass just added
    // (e.g. the Notify Me button + trailing text got removed again, leaving
    // only "Tap "). Primary guard: WeakSet on the event object itself —
    // works even when sourceId is null/undefined (which bypassed the old
    // sourceId-only guard, confirmed root cause of the truncation).
    if (this.#processedEvents.has(event)) return;
    this.#processedEvents.add(event);

    // Secondary sourceId guard: also deduplicate by sourceId (kept for
    // safety — handles the rare case where two distinct event objects carry
    // the same sourceId and both reach this handler).
    const incomingSourceId = event.detail?.sourceId;
    if (incomingSourceId && incomingSourceId === this.#lastProcessedSourceId) return;
    this.#lastProcessedSourceId = incomingSourceId ?? null;
    // Reset on the next microtask so a future genuine re-selection of the
    // same option (same sourceId) still processes.
    if (incomingSourceId) queueMicrotask(() => { if (this.#lastProcessedSourceId === incomingSourceId) this.#lastProcessedSourceId = null; });

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newAvailability = event.detail.data.html.querySelector('product-availability');

    if (!newAvailability) return;

    // Clone before morphing: product-pdp-stitching.js dispatches two separate
    // VariantUpdateEvent objects (section-scoped + document-level) that share
    // the SAME eventData.html reference. morph() moves nodes out of the source
    // (does not clone them), so the second call sees a depleted source and
    // strips whatever the first call just wrote — confirmed root cause of the
    // "Tap " truncation. cloneNode(true) gives each morph call its own fresh
    // copy of the tree so the original is never depleted.
    morph(this, newAvailability.cloneNode(true), { childrenOnly: true });
  };
}

if (!customElements.get('product-availability')) {
  customElements.define('product-availability', ProductAvailability);
}
